// src/admin/components/payments/PaymentForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiMoneyDollarCircleLine,
  RiSaveLine,
  RiCloseLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiErrorWarningLine,
  RiCalendarLine,
  RiUserLine,
  RiFileTextLine
} from 'react-icons/ri';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  createPayment,
  updatePayment,
  getPayment,
  validatePaymentData,
  PAYMENT_TYPE_OPTIONS
} from '../../../models/paymentModel';
import { getAllTenants } from '../../../models/tenantModel';
import PrintReceipt from './PrintReceipt';
import TenantSelectDropdown from './TenantSelectDropdown';

const PaymentForm = ({ isEdit = false }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  const submittingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Data lists
  const [tenants, setTenants] = useState([]);

  // Print receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [createdPayment, setCreatedPayment] = useState(null);

  // Format date properly for backend
  const formatDateForBackend = (date) => {
    if (!date) return '';
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch { return ''; }
  };

  const calculateEndOfMonth = (startDateStr) => {
    if (!startDateStr) return '';
    try {
      const startDate = new Date(startDateStr);
      if (isNaN(startDate.getTime())) return '';
      const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
      return formatDateForBackend(endDate);
    } catch { return ''; }
  };

  const today = new Date();

  // Renter payment data
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentDate: '',
    tenantId: '',
    paymentType: 'rent',
    notes: '',
    paymentPeriodStart: '',
    paymentPeriodEnd: ''
  });

  // Load tenants
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await getAllTenants();
        if (response.success && response.tenants?.tenants) {
          setTenants(Array.isArray(response.tenants.tenants) ? response.tenants.tenants : []);
        } else if (response.success && response.tenants) {
          setTenants(Array.isArray(response.tenants) ? response.tenants : []);
        } else if (Array.isArray(response)) {
          setTenants(response);
        } else {
          setTenants([]);
        }
      } catch (err) {
        console.error('Error loading tenants:', err);
      }
    };
    fetchTenants();
  }, []);

  // Load payment data if editing (renter only)
  useEffect(() => {
    const loadPayment = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const response = await getPayment(id);
          if (response.payment) {
            const payment = response.payment;
            let startDate, endDate;
            if (payment.months) {
              try {
                const months = Array.isArray(payment.months) ? payment.months : JSON.parse(payment.months);
                if (Array.isArray(months) && months.length >= 2) {
                  startDate = months[0]; endDate = months[1];
                } else if (Array.isArray(months) && months.length === 1) {
                  startDate = months[0]; endDate = calculateEndOfMonth(months[0]);
                } else {
                  startDate = formatDateForBackend(today); endDate = calculateEndOfMonth(formatDateForBackend(today));
                }
              } catch {
                startDate = formatDateForBackend(today); endDate = calculateEndOfMonth(formatDateForBackend(today));
              }
            } else {
              startDate = formatDateForBackend(today); endDate = calculateEndOfMonth(formatDateForBackend(today));
            }
            setPaymentData({
              amount: payment.amount || '',
              paymentDate: formatDateForBackend(payment.paymentDate) || formatDateForBackend(today),
              tenantId: payment.tenantId || '',
              paymentType: payment.paymentType || 'rent',
              notes: payment.notes || '',
              paymentPeriodStart: formatDateForBackend(startDate),
              paymentPeriodEnd: formatDateForBackend(endDate)
            });
          }
        } catch (err) {
          setError(t('failed_to_load_payment_data') || 'Failed to load payment data');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    loadPayment();
  }, [isEdit, id, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tenantId') {
      const tenant = tenants.find(t => t.id === value);
      const startDate = formatDateForBackend(tenant?.nextBillingDate || tenant?.paymentDate || today);
      const endDate = calculateEndOfMonth(startDate);
      setPaymentData(prev => ({
        ...prev,
        tenantId: value,
        paymentPeriodStart: startDate,
        paymentPeriodEnd: endDate
      }));
      if (validationErrors.tenantId) setValidationErrors({ ...validationErrors, tenantId: undefined });
      return;
    }
    setPaymentData({ ...paymentData, [name]: value });
    if (validationErrors[name]) setValidationErrors({ ...validationErrors, [name]: undefined });
  };

  const handleDateSelection = (name, date) => {
    const formattedDate = formatDateForBackend(date);
    if (name === 'paymentPeriodStart') {
      setPaymentData({ ...paymentData, paymentPeriodStart: formattedDate, paymentPeriodEnd: calculateEndOfMonth(formattedDate) });
    } else {
      setPaymentData({ ...paymentData, [name]: formattedDate });
    }
    if (validationErrors[name] || (name.startsWith('paymentPeriod') && validationErrors.months)) {
      setValidationErrors({ ...validationErrors, [name]: undefined, months: name.startsWith('paymentPeriod') ? undefined : validationErrors.months });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;

    // Renter payment flow (existing logic)
    const formattedPaymentData = {
      ...paymentData,
      amount: parseFloat(paymentData.amount),
      months: [formatDateForBackend(paymentData.paymentPeriodStart), formatDateForBackend(paymentData.paymentPeriodEnd)]
    };

    const validation = validatePaymentData(formattedPaymentData);
    if (!validation.isValid) { setValidationErrors(validation.errors); return; }

    try {
      setSaving(true);
      const backendData = {
        amount: parseFloat(paymentData.amount),
        paymentDate: formatDateForBackend(paymentData.paymentDate),
        months: [formatDateForBackend(paymentData.paymentPeriodStart), formatDateForBackend(paymentData.paymentPeriodEnd)],
        paymentType: paymentData.paymentType,
        tenantId: paymentData.tenantId,
        notes: paymentData.notes || ''
      };

      if (isEdit) {
        await updatePayment(id, backendData);
        navigate('/admin/payments');
      } else {
        const response = await createPayment(backendData);
        if (response.success && response.payment) {
          const paymentForPrint = {
            ...response.payment,
            tenantName: tenants.find(t => t.id === paymentData.tenantId)?.name || '',
            apartmentNumber: tenants.find(t => t.id === paymentData.tenantId)?.apartmentNumber || '',
            buildingName: tenants.find(t => t.id === paymentData.tenantId)?.buildingName || ''
          };
          setCreatedPayment(paymentForPrint);
          setShowReceipt(true);
        } else {
          navigate('/admin/payments');
        }
      }
    } catch (err) {
      setError(err.message || t('failed_to_save_payment') || 'Failed to save payment');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-zinc-900' : 'bg-white'} rounded-xl border shadow-lg p-8 text-center`}>
        <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
        <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('loading') || 'Loading...'}</p>
      </div>
    );
  }

  const inputClass = (field) => `w-full p-2.5 rounded-lg border ${
    validationErrors[field]
      ? (isDarkMode ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-zinc-50')
      : (isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300')
  }`;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg`}>
      {/* Header */}
      <div className={`p-5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-green-50'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
            {isEdit ? t('edit_payment') || 'Edit Payment' : t('record_payment') || 'Record Payment'}
          </h2>
          <button type="button" onClick={() => navigate('/admin/payments')} className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}>
            <BackArrow size={22} />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'} border flex items-center`}>
          <RiErrorWarningLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-5">
        {/* Payment Information */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('payment_information') || 'Payment Information'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('tenant') || 'Renter'} *
              </label>
              <TenantSelectDropdown
                tenants={tenants} value={paymentData.tenantId}
                onChange={handleInputChange} disabled={loading}
                t={t} isDarkMode={isDarkMode} i18n={i18n}
              />
              {validationErrors.tenantId && <p className={`mt-1 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.tenantId}</p>}
            </div>

            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('amount') || 'Amount'} *</label>
              <input type="number" step="0.01" name="amount" value={paymentData.amount} onChange={handleInputChange} className={inputClass('amount')} required />
              {validationErrors.amount && <p className={`mt-1 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.amount}</p>}
            </div>

            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('payment_date') || 'Payment Date'} *</label>
              <div className="relative">
                <DatePicker
                  selected={paymentData.paymentDate ? new Date(paymentData.paymentDate) : null}
                  onChange={(date) => handleDateSelection('paymentDate', date)}
                  dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy" required
                  showMonthDropdown showYearDropdown dropdownMode="select" isClearable portalId="root-portal"
                  className={inputClass('paymentDate')}
                />
                <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none`} />
              </div>
              {validationErrors.paymentDate && <p className={`mt-1 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.paymentDate}</p>}
            </div>

            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('payment_type') || 'Payment Type'} *</label>
              <select name="paymentType" value={paymentData.paymentType} onChange={handleInputChange} className={inputClass('paymentType')} required>
                {PAYMENT_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{t(option.value) || option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>


        {/* Additional Information (Notes) */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiFileTextLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('additional_information') || 'Additional Information'}
          </h3>
          <div>
            <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('notes') || 'Notes'}</label>
            <textarea
              name="notes"
              value={paymentData.notes}
              onChange={handleInputChange}
              rows={3}
              className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder={t('notes_placeholder') || 'Any additional notes about this payment...'}
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
          <button
            type="button"
            onClick={() => navigate('/admin/payments')}
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
            disabled={saving}
          >
            <RiCloseLine className={`${isRTL ? 'ml-1' : 'mr-1'} inline`} size={18} />
            {t('cancel') || 'Cancel'}
          </button>

          <button
            type="submit"
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors flex items-center`}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className={`h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                {t('saving') || 'Saving...'}
              </>
            ) : (
              <>
                <RiSaveLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {isEdit ? t('update_payment') || 'Update Payment' : t('record_payment') || 'Record Payment'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Print Receipt Modal */}
      {showReceipt && createdPayment && (
        <PrintReceipt
          payment={createdPayment}
          type={createdPayment.paymentType || 'rent'}
          onClose={() => { setShowReceipt(false); navigate('/admin/payments'); }}
          autoPrint={true}
        />
      )}
    </div>
  );
};

export default PaymentForm;